import { useState, useEffect } from 'react';
import { Plus, Wallet, TrendingUp, TrendingDown, Edit2, Trash2, Archive, CheckCircle, Upload } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { parseOFXFile, type OFXParseResult } from '../utils/ofxParser';
import EnhancedImportModal from './EnhancedImportModal';

interface TreasuryAccount {
  id: string;
  name: string;
  account_type: 'current' | 'savings' | 'cash' | 'other';
  feed_mode: 'manual' | 'aggregated';
  initial_balance: number;
  initial_balance_date: string;
  current_balance: number;
  included_in_treasury: boolean;
  aggregation_source: string | null;
  account_number: string | null;
  notes: string;
  is_active: boolean;
  last_updated_at: string;
}

interface ConsolidatedTreasury {
  total: number;
  active_accounts: number;
  manual_total: number;
  aggregated_total: number;
}

const ACCOUNT_TYPE_LABELS = {
  current: 'Compte courant',
  savings: 'Compte épargne',
  cash: 'Caisse',
  other: 'Autre'
};

export default function TreasuryAccountsManager() {
  const [accounts, setAccounts] = useState<TreasuryAccount[]>([]);
  const [consolidatedTreasury, setConsolidatedTreasury] = useState<ConsolidatedTreasury>({
    total: 0,
    active_accounts: 0,
    manual_total: 0,
    aggregated_total: 0
  });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<TreasuryAccount | null>(null);
  const [importingAccountId, setImportingAccountId] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [ofxParseResult, setOfxParseResult] = useState<OFXParseResult | null>(null);
  const [importFileName, setImportFileName] = useState<string>('');

  useEffect(() => {
    loadAccounts();
    loadConsolidatedTreasury();
  }, []);

  const loadAccounts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('treasury_accounts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAccounts(data || []);
    } catch (error: any) {
      console.error('Error loading accounts:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadConsolidatedTreasury = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .rpc('calculate_consolidated_treasury', { p_user_id: user.id });

      if (error) throw error;

      const { data: summary } = await supabase
        .from('v_treasury_summary')
        .select('*')
        .eq('user_id', user.id)
        .single();

      setConsolidatedTreasury({
        total: data || 0,
        active_accounts: summary?.included_accounts_count || 0,
        manual_total: summary?.manual_accounts_total || 0,
        aggregated_total: summary?.aggregated_accounts_total || 0
      });
    } catch (error: any) {
      console.error('Error loading consolidated treasury:', error.message);
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce compte ?')) return;

    try {
      const { error } = await supabase
        .from('treasury_accounts')
        .delete()
        .eq('id', accountId);

      if (error) throw error;

      loadAccounts();
      loadConsolidatedTreasury();
    } catch (error: any) {
      alert(`Erreur: ${error.message}`);
    }
  };

  const handleToggleActive = async (account: TreasuryAccount) => {
    try {
      const { error } = await supabase
        .from('treasury_accounts')
        .update({ is_active: !account.is_active })
        .eq('id', account.id);

      if (error) throw error;

      loadAccounts();
      loadConsolidatedTreasury();
    } catch (error: any) {
      alert(`Erreur: ${error.message}`);
    }
  };

  const handleImportClick = (accountId: string) => {
    setImportingAccountId(accountId);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.ofx,.qfx';
    input.onchange = async (e: any) => {
      const file = e.target?.files?.[0];
      if (!file) return;

      try {
        const result = await parseOFXFile(file);

        if (!result.success || result.transactions.length === 0) {
          alert(result.error || 'Aucune transaction trouvée dans le fichier OFX');
          return;
        }

        setOfxParseResult(result);
        setImportFileName(file.name);
        setShowImportModal(true);
      } catch (error: any) {
        alert(`Erreur lors de la lecture du fichier: ${error.message}`);
      }
    };
    input.click();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Trésorerie consolidée */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-blue-100 text-sm">Trésorerie disponible</p>
            <h2 className="text-4xl font-bold mt-1">
              {formatCurrency(consolidatedTreasury.total)}
            </h2>
          </div>
          <div className="bg-blue-500/30 rounded-full p-3">
            <Wallet className="h-8 w-8" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-blue-500/30">
          <div>
            <p className="text-blue-100 text-xs">Comptes actifs</p>
            <p className="text-2xl font-semibold mt-1">{consolidatedTreasury.active_accounts}</p>
          </div>
          <div>
            <p className="text-blue-100 text-xs">Comptes manuels</p>
            <p className="text-lg font-semibold mt-1">{formatCurrency(consolidatedTreasury.manual_total)}</p>
          </div>
          <div>
            <p className="text-blue-100 text-xs">Comptes agrégés</p>
            <p className="text-lg font-semibold mt-1">{formatCurrency(consolidatedTreasury.aggregated_total)}</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Mes comptes de trésorerie</h3>
          <p className="text-sm text-gray-600 mt-1">
            Gérez vos comptes bancaires et caisses
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Nouveau compte
        </button>
      </div>

      {/* Liste des comptes */}
      {accounts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <Wallet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun compte de trésorerie</h3>
          <p className="text-gray-600 mb-6">
            Créez votre premier compte pour commencer à gérer votre trésorerie
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Créer un compte
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {accounts.map((account) => (
            <div
              key={account.id}
              className={`bg-white border rounded-xl p-6 transition-all ${
                account.is_active ? 'border-gray-200' : 'border-gray-100 bg-gray-50 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">{account.name}</h4>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      account.feed_mode === 'manual'
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {account.feed_mode === 'manual' ? 'Manuel' : 'Agrégé'}
                    </span>
                    {account.included_in_treasury && (
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Inclus dans trésorerie
                      </span>
                    )}
                    {!account.is_active && (
                      <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700">
                        Archivé
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 mb-3">
                    <p className="text-sm text-gray-600">
                      {ACCOUNT_TYPE_LABELS[account.account_type]}
                    </p>
                    {account.account_number && (
                      <>
                        <span className="text-gray-300">•</span>
                        <p className="text-sm text-gray-600 font-mono">
                          {account.account_number}
                        </p>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-xs text-gray-500">Solde actuel</p>
                      <p className={`text-2xl font-bold ${
                        account.current_balance >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(account.current_balance)}
                      </p>
                    </div>

                    <div className="h-12 w-px bg-gray-200" />

                    <div>
                      <p className="text-xs text-gray-500">Solde initial</p>
                      <p className="text-sm font-medium text-gray-700">
                        {formatCurrency(account.initial_balance)}
                      </p>
                      <p className="text-xs text-gray-500">
                        au {new Date(account.initial_balance_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>

                  {account.notes && (
                    <p className="text-sm text-gray-600 mt-3 italic">
                      {account.notes}
                    </p>
                  )}

                  <p className="text-xs text-gray-400 mt-3">
                    Dernière mise à jour : {new Date(account.last_updated_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleImportClick(account.id)}
                    className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Importer OFX"
                  >
                    <Upload className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setEditingAccount(account)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(account)}
                    className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                    title={account.is_active ? 'Archiver' : 'Activer'}
                  >
                    <Archive className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteAccount(account.id)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal d'ajout/édition */}
      {(showAddModal || editingAccount) && (
        <AccountFormModal
          account={editingAccount}
          onClose={() => {
            setShowAddModal(false);
            setEditingAccount(null);
          }}
          onSave={() => {
            setShowAddModal(false);
            setEditingAccount(null);
            loadAccounts();
            loadConsolidatedTreasury();
          }}
        />
      )}

      {/* Modal d'import */}
      {showImportModal && ofxParseResult && importingAccountId && (
        <EnhancedImportModal
          parseResult={ofxParseResult}
          importedFileName={importFileName}
          preselectedAccountId={importingAccountId}
          allowAccountSelection={false}
          allowBalanceUpdate={true}
          onClose={() => {
            setShowImportModal(false);
            setOfxParseResult(null);
            setImportingAccountId(null);
            setImportFileName('');
          }}
          onConfirm={() => {
            setShowImportModal(false);
            setOfxParseResult(null);
            setImportingAccountId(null);
            setImportFileName('');
            loadAccounts();
            loadConsolidatedTreasury();
          }}
        />
      )}
    </div>
  );
}

interface AccountFormModalProps {
  account: TreasuryAccount | null;
  onClose: () => void;
  onSave: () => void;
}

function AccountFormModal({ account, onClose, onSave }: AccountFormModalProps) {
  const [formData, setFormData] = useState({
    name: account?.name || '',
    account_number: account?.account_number || '',
    account_type: account?.account_type || 'current',
    initial_balance: account?.initial_balance || 0,
    initial_balance_date: account?.initial_balance_date || new Date().toISOString().split('T')[0],
    included_in_treasury: account?.included_in_treasury ?? true,
    notes: account?.notes || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      if (account) {
        // Mise à jour
        const { error } = await supabase
          .from('treasury_accounts')
          .update({
            name: formData.name,
            account_number: formData.account_number || null,
            account_type: formData.account_type,
            initial_balance: formData.initial_balance,
            initial_balance_date: formData.initial_balance_date,
            current_balance: formData.initial_balance, // On met à jour le solde courant aussi
            included_in_treasury: formData.included_in_treasury,
            notes: formData.notes
          })
          .eq('id', account.id);

        if (error) throw error;
      } else {
        // Création
        const { error } = await supabase
          .from('treasury_accounts')
          .insert({
            user_id: user.id,
            name: formData.name,
            account_number: formData.account_number || null,
            account_type: formData.account_type,
            feed_mode: 'manual',
            initial_balance: formData.initial_balance,
            initial_balance_date: formData.initial_balance_date,
            current_balance: formData.initial_balance,
            included_in_treasury: formData.included_in_treasury,
            notes: formData.notes
          });

        if (error) throw error;
      }

      onSave();
    } catch (error: any) {
      alert(`Erreur: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">
            {account ? 'Modifier le compte' : 'Nouveau compte de trésorerie'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom du compte *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Compte courant CA, Caisse espèces..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Numéro de compte
            </label>
            <input
              type="text"
              value={formData.account_number}
              onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
              placeholder="Ex: FR76 1234 5678 9012 3456 7890 123"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
            />
            <p className="text-xs text-gray-500 mt-1">
              IBAN, RIB ou autre identifiant bancaire (optionnel)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de compte *
            </label>
            <select
              required
              value={formData.account_type}
              onChange={(e) => setFormData({ ...formData, account_type: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="current">Compte courant</option>
              <option value="savings">Compte épargne</option>
              <option value="cash">Caisse</option>
              <option value="other">Autre</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Solde initial *
            </label>
            <input
              type="number"
              step="0.01"
              required
              value={formData.initial_balance}
              onChange={(e) => setFormData({ ...formData, initial_balance: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date du solde initial *
            </label>
            <input
              type="date"
              required
              value={formData.initial_balance_date}
              onChange={(e) => setFormData({ ...formData, initial_balance_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.included_in_treasury}
                onChange={(e) => setFormData({ ...formData, included_in_treasury: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Inclure ce compte dans la trésorerie consolidée
              </span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optionnel)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Informations complémentaires..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={saving}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : account ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
